import React, { useState, useEffect, useMemo } from "react";
import { Descriptions, Typography } from "antd";
import { useParams } from "react-router-dom";
import CustomCard from "../../../components/CustomCard";
import {
  getMetadataLabel,
  calculatePremiumCapacity,
  type PremiumCapacityResponse,
  useProductCompanyStore,
  useConfigStore,
} from "@ifa/utils";
import { isZuuFnaVariant } from "../../order-fna/fnaVariant.js";
import { normalizeOrderFinancialForDisplay } from "../../order-fna/zuuFnaDetailData.js";
import { FinancialForm } from "../../OrderForms/FinancialForm";
import styles from "./FinancialView.module.css";

const { Text } = Typography;

interface FinancialViewProps {
  financial: any;
  currentFinancialStep: "financial-info" | "fna";
  orderData?: any;
}

const cx = (...classNames: Array<string | false | null | undefined>) => classNames.filter(Boolean).join(" ");

export function FinancialView({
  financial,
  currentFinancialStep,
  orderData,
}: FinancialViewProps) {
  const { orderId } = useParams<{ orderId: string }>();
  const [premiumCapacity, setPremiumCapacity] = useState<PremiumCapacityResponse | null>(null);
  const { productCompanyList, fetchProductCompanyList } = useProductCompanyStore();
  const brandId = useConfigStore((state) => state.brandId);
  const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
  const isZuuMode = isZuuFnaVariant({ brandId, tenantCode });
  const normalizedFinancial = useMemo(
    () => (isZuuMode ? normalizeOrderFinancialForDisplay(financial) : financial),
    [financial, isZuuMode],
  );
  const isZuuFnaDetailDisplay = currentFinancialStep !== "fna";

  // 获取产品公司列表
  useEffect(() => {
    fetchProductCompanyList();
  }, [fetchProductCompanyList]);

  // 根据公司代码获取公司名称
  const getCompanyName = (companyCode: string | undefined) => {
    if (!companyCode) return "-";
    const company = productCompanyList.find((c) => c.code === companyCode);
    return company?.name || companyCode;
  };

  const formatPremiumLoanAmount = (item: any) => {
    if (!item?.loanAmount) return "-";
    return `${item.loanAmount} ${item.loanAmountCurrency || "HKD"}`;
  };

  // 获取保费承担能力数据
  useEffect(() => {
    const fetchPremiumCapacity = async () => {
      if (
        !orderId ||
        !normalizedFinancial.financialAnalysis ||
        normalizedFinancial.financialAnalysis.length === 0
      ) {
        return;
      }
      try {
        const response = await calculatePremiumCapacity({
          orderId: Number(orderId),
          financialAnalysis: normalizedFinancial.financialAnalysis,
        });
        setPremiumCapacity(response);
      } catch (error) {
        console.error("获取保费承担能力失败:", error);
      }
    };
    fetchPremiumCapacity();
  }, [orderId, normalizedFinancial.financialAnalysis]);

  // Subtotal Calculations
  const totalIncome =
    (normalizedFinancial.income?.salaryCommissionBonus || 0) +
    (normalizedFinancial.income?.rentalIncome || 0) +
    (normalizedFinancial.income?.hasOtherIncome && normalizedFinancial.income?.otherIncomeAmount ? normalizedFinancial.income.otherIncomeAmount : 0);
  const totalExpense =
    (normalizedFinancial.expense?.personalAndFamilyExpense || 0) +
    (normalizedFinancial.expense?.mortgageLoanRepayment || 0) +
    (normalizedFinancial.expense?.insuranceContribution || 0) +
    (normalizedFinancial.expense?.hasOtherExpense && normalizedFinancial.expense?.otherExpenseAmount ? normalizedFinancial.expense.otherExpenseAmount : 0);
  const totalCurrentAssets =
    (normalizedFinancial.currentAssets?.cashAndDeposits || 0) +
    (normalizedFinancial.currentAssets?.liquidInvestments || 0) +
    (normalizedFinancial.currentAssets?.hasOtherCurrentAssets && normalizedFinancial.currentAssets?.otherCurrentAssetsAmount ? normalizedFinancial.currentAssets.otherCurrentAssetsAmount : 0);
  const totalNonCurrentAssets = (normalizedFinancial.nonCurrentAssets?.property || 0) + (normalizedFinancial.nonCurrentAssets?.otherNonCurrentAssets || 0);

  const totalLiabilities =
    (normalizedFinancial.liabilities?.mortgageLoan || 0) +
    (normalizedFinancial.liabilities?.personalCreditCardLoan || 0) +
    (normalizedFinancial.liabilities?.hasOtherLiabilities && normalizedFinancial.liabilities?.otherLiabilityAmount ? normalizedFinancial.liabilities.otherLiabilityAmount : 0);

  const surplusDeficit = totalIncome - totalExpense;
  const netAssets = totalCurrentAssets + totalNonCurrentAssets - totalLiabilities;

  const formatCurrency = (val: number) => {
    return val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatInteger = (val: number) => {
    return val.toLocaleString("en-US", { maximumFractionDigits: 0 });
  };
  if (isZuuMode) {
    return (
      <div className={styles["detail-content"]}>
        <FinancialForm
          data={normalizedFinancial || {}}
          onChange={() => {}}
          isFinancialInfoRequired={
            orderData?.isFinancialInfoRequired ?? true
          }
          readOnly={true}
          detailDisplay={isZuuFnaDetailDisplay}
          activeSection={currentFinancialStep}
          orderData={orderData || {}}
        />
      </div>
    );
  }

  const renderFinancialInfo = () => (
    <div className={styles["detail-content"]}>
      <CustomCard title="一、財務資料" bordered={false} className={cx(styles["detail-card"], styles["mb-4"])}>
        <div className={styles.summaryBanner}>
          <span>1. 估計每月盈餘/超支 (HK$/港幣)</span>
          <span>A-B = {surplusDeficit.toLocaleString()} HKD</span>
        </div>

        {/* Income Section */}
        <div className={styles.sectionPanel}>
          <div className={cx(styles.sectionHeader, styles.sectionHeaderTeal)}>
            <span className={styles.sectionHeaderTitle}>A) 過去2年的平均每月收入</span>
            <span className={styles.sectionHeaderValue}>小計:{totalIncome.toLocaleString()} HKD</span>
          </div>
          <Descriptions column={2} colon={false} layout="vertical">
            <Descriptions.Item label="薪金/佣金/花紅">{financial.income?.salaryCommissionBonus?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="租金收入">{financial.income?.rentalIncome?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="其他收入名稱">{financial.income?.hasOtherIncome ? financial.income?.otherIncomeName : "-"}</Descriptions.Item>
            <Descriptions.Item label="其他收入金額">{financial.income?.hasOtherIncome ? financial.income?.otherIncomeAmount?.toLocaleString() : "-"}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Expense Section */}
        <div className={styles.sectionPanel}>
          <div className={cx(styles.sectionHeader, styles.sectionHeaderAccent)}>
            <span className={styles.sectionHeaderTitle}>B) 過去2年的平均每月支出</span>
            <span className={styles.sectionHeaderValue}>小計:{totalExpense.toLocaleString()} HKD</span>
          </div>
          <Descriptions column={2} colon={false} layout="vertical">
            <Descriptions.Item label="個人及家庭開支">{financial.expense?.personalAndFamilyExpense?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="按揭/借貸還款">{financial.expense?.mortgageLoanRepayment?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="保險供款">{financial.expense?.insuranceContribution?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="其他支出名稱">{financial.expense?.hasOtherExpense ? financial.expense?.otherExpenseName : "-"}</Descriptions.Item>
            <Descriptions.Item label="其他支出金額">{financial.expense?.hasOtherExpense ? financial.expense?.otherExpenseAmount?.toLocaleString() : "-"}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Assets Section */}
        <div className={styles.summaryBanner}>
          <span>2. 資產及負債 (HK$/港幣)</span>
          <span>C+D-E = {netAssets.toLocaleString()} HKD</span>
        </div>

        {/* Current Assets (C) */}
        <div className={styles.sectionPanel}>
          <div className={cx(styles.sectionHeader, styles.sectionHeaderTeal)}>
            <span className={styles.sectionHeaderTitle}>C) 流動資產</span>
            <span className={styles.sectionHeaderValue}>小計:{totalCurrentAssets.toLocaleString()} HKD</span>
          </div>
          <Descriptions column={2} colon={false} layout="vertical">
            <Descriptions.Item label="現金及存款">{financial.currentAssets?.cashAndDeposits?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="變現能力高的投資 (股票/債券等)">{financial.currentAssets?.liquidInvestments?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="其他流動資產名稱">{financial.currentAssets?.hasOtherCurrentAssets ? financial.currentAssets?.otherCurrentAssetsName : "-"}</Descriptions.Item>
            <Descriptions.Item label="其他流動資產金額">{financial.currentAssets?.hasOtherCurrentAssets ? financial.currentAssets?.otherCurrentAssetsAmount?.toLocaleString() : "-"}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Non-Current Assets (D) */}
        <div className={styles.sectionPanel}>
          <div className={cx(styles.sectionHeader, styles.sectionHeaderTeal)}>
            <span className={styles.sectionHeaderTitle}>D) 非流動資產</span>
            <span className={styles.sectionHeaderValue}>小計:{totalNonCurrentAssets.toLocaleString()} HKD</span>
          </div>
          <Descriptions column={2} colon={false} layout="vertical">
            <Descriptions.Item label="物業">{financial.nonCurrentAssets?.property?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="其他 (如公司股份)">{financial.nonCurrentAssets?.otherNonCurrentAssets?.toLocaleString() || "-"}</Descriptions.Item>
          </Descriptions>
        </div>

        {/* Liabilities (E) */}
        <div className={styles.sectionPanel}>
          <div className={cx(styles.sectionHeader, styles.sectionHeaderTeal)}>
            <span className={styles.sectionHeaderTitle}>E) 負債</span>
            <span className={styles.sectionHeaderValue}>小計:{totalLiabilities.toLocaleString()} HKD</span>
          </div>
          <Descriptions column={2} colon={false} layout="vertical">
            <Descriptions.Item label="按揭貸款">{financial.liabilities?.mortgageLoan?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="私人/信用卡貸款">{financial.liabilities?.personalCreditCardLoan?.toLocaleString() || "-"}</Descriptions.Item>
            <Descriptions.Item label="其他負債名稱">{financial.liabilities?.hasOtherLiabilities ? financial.liabilities?.otherLiabilityName : "-"}</Descriptions.Item>
            <Descriptions.Item label="其他負債金額">{financial.liabilities?.hasOtherLiabilities ? financial.liabilities?.otherLiabilityAmount?.toLocaleString() : "-"}</Descriptions.Item>
          </Descriptions>
        </div>
      </CustomCard>

      <CustomCard title="二、現行保障 (HK$/港幣)" bordered={false} className={cx(styles["detail-card"], styles["mb-4"])}>
        <Descriptions column={2} colon={false} layout="vertical">
          <Descriptions.Item label="人壽保險">{financial.protection?.lifeInsurance || "-"}</Descriptions.Item>
          <Descriptions.Item label="危疾保險">{financial.protection?.criticalIllnessInsurance || "-"}</Descriptions.Item>
          <Descriptions.Item label="醫療保險類別">{getMetadataLabel("MEDICAL_INSURANCE_TYPE", financial.protection?.medicalInsuranceType)}</Descriptions.Item>
          <Descriptions.Item label="醫療保險金額">{financial.protection?.medicalInsuranceAmount?.toLocaleString() || "-"}</Descriptions.Item>
          <Descriptions.Item label="意外保險">{financial.protection?.accidentInsurance || "-"}</Descriptions.Item>
          <Descriptions.Item label="其他現有保障">{financial.protection?.otherExistingProtection || "-"}</Descriptions.Item>
          <Descriptions.Item label="附件資料/備註">{financial.protection?.attachmentRemarks || "-"}</Descriptions.Item>
        </Descriptions>
      </CustomCard>
    </div>
  );

  const renderFnaInfo = () => {
    const fnaData = Array.isArray(normalizedFinancial.financialAnalysis) ? normalizedFinancial.financialAnalysis : [];
    const transferData = Array.isArray(normalizedFinancial.transferInsuranceDeclaration) ? normalizedFinancial.transferInsuranceDeclaration : [];
    const suggestionsData = Array.isArray(normalizedFinancial.suggestionsAndReasons) ? normalizedFinancial.suggestionsAndReasons : [];
    const allQuestionData = [...fnaData, ...transferData, ...suggestionsData];

    // Insurance objectives mapping
    const insuranceObjectives = [
      { value: "A", label: "少於1年" },
      { value: "B", label: "1-5年" },
      { value: "C", label: "6-10年" },
      { value: "D", label: "11-15年" },
      { value: "E", label: "16-20年" },
      { value: "F", label: "超過20年" },
    ];

    // Investment management preferences
    const investmentMgmtOptions = [
      { value: "A", label: "本人願意按個人決定選擇及管理保險產品項下的不同投資選項/投資選擇（如有），並且願意在保險產品的目標利益/保障期的整個期間作出此決定。" },
      { value: "B", label: "本人不願意選擇或管理保險產品項下的不同投資選項/投資選擇（如有）。" },
    ];

    // Time period options
    const timePeriodOptions = [
      { value: "A", label: "少於1年" },
      { value: "B", label: "1-5年" },
      { value: "C", label: "6-10年" },
      { value: "D", label: "11-15年" },
      { value: "E", label: "16-20年" },
      { value: "F", label: "超過20年" },
      { value: "G", label: "終身" },
    ];

    // Premium payment period options
    const premiumPeriodOptions = [
      { value: "A", label: "2-5年" },
      { value: "B", label: "6-10年" },
      { value: "C", label: "11-15年" },
      { value: "D", label: "16-20年" },
      { value: "E", label: "超過20年" },
      { value: "F", label: "終身" },
      { value: "G", label: "一次性付款不超過" },
    ];

    // Income ratio options
    const incomeRatioOptions = [
      { value: "A", label: "少於10%" },
      { value: "B", label: "10%-20%" },
      { value: "C", label: "21%-30%" },
      { value: "D", label: "31%-40%" },
      { value: "E", label: "41%-50%" },
      { value: "F", label: "超過50%" },
      { value: "G", label: "不適用" },
    ];

    // Investment withdrawal options
    const withdrawalOptions = [
      { value: "A", label: "20年後" },
      { value: "B", label: "十至二十年內" },
      { value: "C", label: "五至十年內" },
      { value: "D", label: "五年內但現在不提取" },
      { value: "E", label: "立即提取" },
    ];

    // Investment goal options
    const investmentGoalOptions = [
      { value: "A", label: "積極增長" },
      { value: "B", label: "顯著增長" },
      { value: "C", label: "適度增長" },
      { value: "D", label: "輕微增長" },
      { value: "E", label: "避免虧損" },
    ];

    // Investment expectation options
    const investmentExpectationOptions = [
      { value: "A", label: "虧損" },
      { value: "B", label: "微小收益或無收益" },
      { value: "C", label: "小幅收益" },
      { value: "D", label: "適度收益" },
      { value: "E", label: "輕微受市場影響" },
    ];

    // Continued contribution reasons
    const contributionReasonOptions = [
      { value: "A", label: "保費會根據退休時的實際需要，由退休儲蓄金支付。" },
      { value: "B", label: "其他" },
    ];

    // Transfer insurance assessment options
    const transferAssessmentOptions = [
      { value: "A", label: "在檢視了客戶現有的人壽保險之後，客戶認為新的計劃更為合適(如享有更佳的保障/更靈活的保費繳付模式/較大比例的保證回報/多元化產品特徵)，客人已清楚了解轉保的影響和相關風險" },
      { value: "B", label: "其他" },
    ];

    // Helper function to get selected keys from checkbox/radio questions
    const getSelectedKeys = (questionKey: string) => {
      const question = allQuestionData.find((q: any) => q.question === questionKey);
      if (!question) return [];
      return question.items?.filter((item: any) => item.value === true).map((item: any) => item.key) || [];
    };

    const getSelectedKey = (questionKey: string) => {
      const keys = getSelectedKeys(questionKey);
      return keys.length > 0 ? keys[0] : undefined;
    };

    const getInputValue = (questionKey: string) => {
      const question = allQuestionData.find((q: any) => q.question === questionKey);
      return question?.items?.find((i: any) => i.key === "A")?.value;
    };

    // Convert key to ratio value
    const getRatioFromKey = (key: string | undefined): number => {
      switch (key) {
        case "A":
          return 0.1; // 少於10%
        case "B":
          return 0.2; // 10%-20%
        case "C":
          return 0.3; // 21%-30%
        case "D":
          return 0.4; // 31%-40%
        case "E":
          return 0.5; // 41%-50%
        case "F":
          return 1.0; // 超過50%
        default:
          return 0;
      }
    };

    const ratioA = getRatioFromKey(getSelectedKey("premiumToDisposableIncomeRatio"));
    const ratioB = getRatioFromKey(getSelectedKey("premiumToLiquidAssetsRatio"));

    // 使用 API 数据，如果 API 数据不可用则使用本地计算作为后备
    const a_monthly = premiumCapacity?.monthlyDisposableIncome ?? surplusDeficit;
    const a_yearly = premiumCapacity?.annualDisposableIncome ?? surplusDeficit * 12;
    const a1_monthly = premiumCapacity?.maxMonthlyPremium ?? a_monthly * ratioA;
    const a1_yearly = premiumCapacity?.maxAnnualPremium ?? a_yearly * ratioA;

    const b_assets = premiumCapacity?.liquidAssets ?? totalCurrentAssets;
    const b1_amount = premiumCapacity?.maxLiquidAssetPremium ?? b_assets * ratioB;

    return (
      <div
        className={cx(
          styles["detail-content"],
          isZuuMode && styles.zuuDetailMode,
        )}
      >
        {isZuuMode ? (
          <div className={styles.zuuHero}>
            <div className={styles.zuuEyebrow}>ZUU FNA viewing mode</div>
            <div className={styles.zuuHeroTitle}>ZUU 財務需要分析查看頁</div>
            <div className={styles.zuuHeroDescription}>
              保留原有 FNA / 保費融資 / 轉保聲明 / 產品結果資料語義，改以
              ZUU 策略下的查看態樣式呈現。
            </div>
          </div>
        ) : null}
        {/* Card 1: 三、財務需要分析 (FNA) - Questions 1-4 */}
        <CustomCard
          title="三、財務需要分析 (FNA)"
          bordered={false}
          className={cx(styles["detail-card"], isZuuMode && styles.zuuDetailCard)}
        >
          {/* 1. Insurance Purchase Objectives */}
          <div className={styles.questionBlock}>
            <div className={cx(styles.questionTitle, styles.questionTitleLarge)}>1. 購買保險產品的目標</div>
            <div className={styles.questionTitle}>閣下購買保險產品的目標為何？ (勾選一項或多項)</div>
            <div className={styles.verticalList16}>
              {insuranceObjectives
                .filter((obj) => getSelectedKeys("insurancePurchaseObjectives").includes(obj.value))
                .map((obj) => (
                  <div key={obj.value} className={cx(styles.choiceCard, styles.choiceCardMd)}>
                    <div className={styles.choiceCardTitle}>
                      {obj.value}. {obj.label}
                    </div>

                    {/* Additional fields for specific objectives */}
                    {obj.value === "A" && (
                      <div className={styles.detailIndent24}>
                        <Text type="secondary" className={styles.smallText}>
                          額外人壽保障額 (HK$/港幣): {getInputValue("additionalLifeCoverageAmount")?.toLocaleString() || "-"}
                        </Text>
                      </div>
                    )}

                    {obj.value === "B" && (
                      <div className={styles.detailIndent24}>
                        <Text type="secondary" className={styles.smallText}>
                          額外危疾保障額 (HK$/港幣): {getInputValue("additionalCriticalIllnessCoverageAmount")?.toLocaleString() || "-"}
                        </Text>
                      </div>
                    )}

                    {(obj.value === "C" || obj.value === "D") && (
                      <div className={styles.detailIndent24}>
                        <div className={styles.gridTwo16}>
                          <div>
                            <Text type="secondary" className={styles.smallText}>
                              年齡/年期: {getInputValue("additionalSavingsTargetReturnPeriod") || "-"}
                            </Text>
                          </div>
                          <div>
                            <Text type="secondary" className={styles.smallText}>
                              額外回報金額(HK$/港幣): {getInputValue("additionalSavingsTargetReturnAmount")?.toLocaleString() || "-"}
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}

                    {obj.value === "E" && (
                      <div className={styles.detailIndent24}>
                        <div className={styles.gridTwo16}>
                          <div>
                            <Text type="secondary" className={styles.smallText}>
                              年齡/年期: {getInputValue("additionalInvestmentTargetReturnPeriod") || "-"}
                            </Text>
                          </div>
                        </div>
                      </div>
                    )}

                    {obj.value === "F" && (
                      <div className={styles.detailIndent24}>
                        <Text type="secondary" className={styles.smallText}>
                          請詳述: {getInputValue("insurancePurchaseOtherObjectives") || "-"}
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
              {getSelectedKeys("insurancePurchaseObjectives").length === 0 && <div className={styles.mutedEmptyState}>未選擇任何選項</div>}
            </div>
          </div>
          <div className={styles.questionBlock}>
            <div className={styles.questionTitle}>1.E.1 閣下希望如何管理保險產品項目下的不同投資選項 / 投資選擇（如有）？（勾選一項）。</div>
            <div className={styles.verticalList12}>
              {investmentMgmtOptions
                .filter((option) => getSelectedKey("investmentOptionManagementPreference") === option.value)
                .map((option) => (
                  <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardMd)}>
                    {option.label}

                    {/* Nested questions for option A */}
                    {option.value === "A" && (
                      <div className={styles.nestedWhitePanel}>
                        <div className={styles.bottomMargin16}>
                          <Text className={cx(styles.smallText, styles.secondaryText)}>1.E.1.1 閣下計劃何時開始提取閣下的投資？(勾選一項)。</Text>
                          <div className={cx(styles.topMargin8, styles.bodyText, styles.bodyTextSm)}>
                            {withdrawalOptions.find((opt) => opt.value === getSelectedKey("investmentWithdrawalStartTime"))?.label || "-"}
                          </div>
                        </div>
                        <div className={styles.bottomMargin16}>
                          <Text className={cx(styles.smallText, styles.secondaryText)}>1.E.1.2 閣下希望此保險產品能達致什麼長遠目標？(勾選一項)。</Text>
                          <div className={cx(styles.topMargin8, styles.bodyText, styles.bodyTextSm)}>
                            {investmentGoalOptions.find((opt) => opt.value === getSelectedKey("investmentLongTermGoal"))?.label || "-"}
                          </div>
                        </div>
                        <div>
                          <Text className={cx(styles.smallText, styles.secondaryText)}>1.E.1.3 假設投資市場表現欠佳，閣下預期投資回報會是？(勾選一項)。</Text>
                          <div className={cx(styles.topMargin8, styles.bodyText, styles.bodyTextSm)}>
                            {investmentExpectationOptions.find((opt) => opt.value === getSelectedKey("investmentExpectationIfMarketPoor"))?.label || "-"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              {!getSelectedKey("investmentOptionManagementPreference") && <div className={styles.mutedEmptyState}>未選擇任何選項</div>}
            </div>
          </div>
          <div className={styles.questionBlock}>
            <div className={cx(styles.questionTitle, styles.questionTitleLarge)}>2. 目標得益/保障年期</div>
            <div className={styles.questionTitle}>閣下投購保單的目標得益/保障年期/實現目標金額的預期時間為多久？（勾選一項）。</div>
            <div className={styles.gridTwo12}>
              {timePeriodOptions
                .filter((option) => getSelectedKey("policyTargetExpectedTime") === option.value)
                .map((option) => (
                  <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm, styles.fontWeight500)}>
                    {option.label}
                  </div>
                ))}
              {!getSelectedKey("policyTargetExpectedTime") && <div className={cx(styles.mutedEmptyState, styles.gridFullColumn)}>未選擇任何選項</div>}
            </div>
          </div>

          {/* 3. Premium Payment Ability */}
          <div className={styles.questionBlock}>
            <div className={cx(styles.questionTitle, styles.questionTitleLarge)}>3. 閣下支付保費的能力</div>
            <div className={styles.questionBlock}>
              <div className={styles.questionTitle}>
                3.1 在整個保單期內，閣下能夠及願意繳付的保費（包括閣下現有的其他保單）佔透過所有收入來源（包括流動資產收入）獲得的每月可動用收入（即經扣除開支）的比率為？（勾選一項）。
              </div>
              <div className={styles.gridTwo12}>
                {incomeRatioOptions
                  .filter((option) => getSelectedKey("premiumToDisposableIncomeRatio") === option.value)
                  .map((option) => (
                    <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm, styles.fontWeight500)}>
                      {option.label}
                    </div>
                  ))}
                {!getSelectedKey("premiumToDisposableIncomeRatio") && <div className={cx(styles.mutedEmptyState, styles.gridFullColumn)}>未選擇任何選項</div>}
              </div>
              {getSelectedKey("premiumToDisposableIncomeRatio") === "G" && (
                <div className={styles.topMargin8}>
                  <Text type="secondary" className={styles.smallText}>
                    原因: {getInputValue("premiumToIncomeRatioNotApplicableReason") || "-"}
                  </Text>
                </div>
              )}
            </div>
          </div>
          {/* 3.2 Premium to Liquid Assets Ratio */}
          <div className={styles.questionBlock}>
            <div className={styles.questionTitle}>3.2 在整個保單期內，閣下能夠及願意繳付的保費（不包括閣下現有的其他保單）佔淨流動資產比率為？（勾選一項）。</div>
            <div className={styles.gridTwo12}>
              {incomeRatioOptions
                .filter((opt) => opt.value !== "G" && getSelectedKey("premiumToLiquidAssetsRatio") === opt.value)
                .map((option) => (
                  <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm, styles.fontWeight500)}>
                    {option.label}
                  </div>
                ))}
              {!getSelectedKey("premiumToLiquidAssetsRatio") && <div className={cx(styles.mutedEmptyState, styles.gridFullColumn)}>未選擇任何選項</div>}
            </div>
          </div>

          {/* 3.3 Premium Payment Methods */}
          <div className={styles.questionBlock}>
            <div className={styles.questionTitle}>3.3 阁下打算以何种方式支付保费？</div>
            <div className={styles.optionsWrap}>
              {getSelectedKeys("intendedPremiumPaymentMethods").includes("A") && (
                <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex, styles.choiceCardMinWidth)}>
                  A. 來自收入
                </div>
              )}
              {getSelectedKeys("intendedPremiumPaymentMethods").includes("B") && (
                <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex, styles.choiceCardMinWidth)}>
                  B. 來自流動資產
                </div>
              )}
              {getSelectedKeys("intendedPremiumPaymentMethods").length === 0 && <div className={cx(styles.mutedEmptyState, styles.fullWidth)}>未選擇任何選項</div>}
            </div>
          </div>

          {/* Payment Ability Summary - 只在选择了 A 或 B 时显示 */}
          {(getSelectedKeys("intendedPremiumPaymentMethods").includes("A") || getSelectedKeys("intendedPremiumPaymentMethods").includes("B")) && (
            <div className={styles.summaryPanel}>
              {/* 只在选择了 A 时显示 a 和 a1 */}
              {getSelectedKeys("intendedPremiumPaymentMethods").includes("A") && (
                <>
                  <div className={styles.bottomMargin8}>
                    a. 每月可動用收入: {formatInteger(a_monthly)} HKD/月 {formatInteger(a_yearly)} HKD/年
                  </div>
                  <div className={styles.bottomMargin8}>
                    a1. 每月可動用收入願意繳付保費的最高金額: {formatCurrency(a1_monthly)} HKD/月 {formatCurrency(a1_yearly)} HKD/年
                  </div>
                </>
              )}
              {/* 只在选择了 B 时显示 b 和 b1 */}
              {getSelectedKeys("intendedPremiumPaymentMethods").includes("B") && (
                <>
                  <div className={styles.bottomMargin8}>b. 流動資產: {formatInteger(b_assets)} HKD</div>
                  <div>b1. 流動資產願意繳付保費最高金額: {formatCurrency(b1_amount)} HKD</div>
                </>
              )}
              <div className={styles.warningText}>*請注意，閣下的繳費承擔能力，需要涵蓋正在申請和即將申請的保單總保費</div>
            </div>
          )}

          {/* 4. Premium Payment Willingness */}
          <div className={styles.questionBlock}>
            <div className={cx(styles.questionTitle, styles.questionTitleLarge)}>4. 阁下缴付保费的意愿</div>
            <div className={styles.questionBlock}>
              <div className={styles.questionTitle}>4.1 閣下能夠及願意為保單支付保費的年期為？（勾選一項）</div>
              <div className={styles.gridTwo12}>
                {premiumPeriodOptions
                  .filter((option) => getSelectedKey("willingPremiumPaymentPeriodRange") === option.value)
                  .map((option) => (
                    <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm, styles.fontWeight500)}>
                      {option.label}
                      {option.value === "G" && <div className={styles.choiceCardDetail}>金額: {getInputValue("singlePremiumPaymentLimit")?.toLocaleString() || "-"} HKD</div>}
                    </div>
                  ))}
                {!getSelectedKey("willingPremiumPaymentPeriodRange") && <div className={cx(styles.mutedEmptyState, styles.gridFullColumn)}>未選擇任何選項</div>}
              </div>
            </div>

            <div>
              <div className={styles.questionTitle}>4.2 如供款年期超過一般退休年歲或在保險建議項目上的期望退休年歲，請說明可以持續供款的原因。（勾選一項）</div>
              <div className={styles.verticalList12}>
                {contributionReasonOptions
                  .filter((option) => getSelectedKey("continuedContributionReason") === option.value)
                  .map((option) => (
                    <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm, styles.fontWeight500)}>
                      {option.label}
                      {option.value === "B" && <div className={styles.choiceCardDetail}>詳述: {getInputValue("continuedContributionReasonOther") || "-"}</div>}
                    </div>
                  ))}
                {!getSelectedKey("continuedContributionReason") && <div className={styles.mutedEmptyState}>未選擇任何選項</div>}
              </div>
            </div>
          </div>
        </CustomCard>
        {/* Card 2: 四、保費融資 */}
        <CustomCard
          title="四、保費融資"
          bordered={false}
          className={cx(styles["detail-card"], isZuuMode && styles.zuuDetailCard)}
        >
          {/* Premium Financing */}
          <div>
            <div className={styles.bottomMargin16}>
              <div className={cx(styles.questionTitle, styles.bottomMargin8)}>1. 閣下是否打算運用"保費融資"安排此保單 ? (勾選一項)</div>
              <div className={styles.optionsRow}>
                {financial.isPremiumFinanced === true && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    A. 是
                  </div>
                )}
                {financial.isPremiumFinanced === false && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    B. 否
                  </div>
                )}
                {financial.isPremiumFinanced === undefined && <div className={cx(styles.mutedEmptyState, styles.fullWidth)}>未選擇任何選項</div>}
              </div>
              <Text type="secondary" className={cx(styles.helperText, styles.smallText)}>
                若上方問題之答案為「是」者，請填寫附件"保費融資安排分析"。
              </Text>
            </div>

            {!isZuuMode && (
            <div>
              <div className={cx(styles.questionTitle, styles.bottomMargin8)}>2. 閣下是否有任何現有的保單被用作"保費融資"的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品 ? (勾選一項)</div>
              <div className={styles.optionsRow}>
                {financial.hasExistingPolicyAsMortgage === true && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    A. 是
                  </div>
                )}
                {financial.hasExistingPolicyAsMortgage === false && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    B. 否
                  </div>
                )}
                {financial.hasExistingPolicyAsMortgage === undefined && <div className={cx(styles.mutedEmptyState, styles.fullWidth)}>未選擇任何選項</div>}
              </div>
              <Text type="secondary" className={cx(styles.helperText, styles.smallText)}>
                若上方問題之答案為「是」者，請填寫下列表格告知現有保費融資的抵押保單詳情。
              </Text>
            </div>
            )}

            {!isZuuMode && financial.hasExistingPolicyAsMortgage === true &&
              financial.premiumFinancing?.map((item: any, index: number) => (
                <div key={index} className={styles.recordCard}>
                  <div className={styles.recordTitle}>抵押保單 {index + 1}</div>
                  <Descriptions column={2} layout="vertical">
                    <Descriptions.Item label="保險公司">{getCompanyName(item.insuranceCompany)}</Descriptions.Item>
                    <Descriptions.Item label="保單號碼">{item.policyNo || "-"}</Descriptions.Item>
                    <Descriptions.Item label="貸方名稱">{item.lenderName || "-"}</Descriptions.Item>
                    <Descriptions.Item label="貸款總額">{formatPremiumLoanAmount(item)}</Descriptions.Item>
                    <Descriptions.Item label="貸款利率">{item.loanInterestRate ? `${item.loanInterestRate}%` : "-"}</Descriptions.Item>
                    <Descriptions.Item label="貸款期限">{item.loanTerm || "-"}</Descriptions.Item>
                    <Descriptions.Item label="還款項">{item.repaymentAmount ? `${item.repaymentAmount} HKD` : "-"}</Descriptions.Item>
                    <Descriptions.Item label="備註">{item.remark || "-"}</Descriptions.Item>
                  </Descriptions>
                </div>
              ))}
          </div>
        </CustomCard>
        {/* Card 3: 五、轉保聲明 */}
        <CustomCard
          title="五、轉保聲明"
          bordered={false}
          className={cx(styles["detail-card"], isZuuMode && styles.zuuDetailCard)}
        >
          {/* Transfer Insurance Declaration */}
          <div>
            <div className={styles.bottomMargin16}>
              <div className={cx(styles.questionTitle, styles.bottomMargin8)}>
                1. 閣下是否使用或打算使用現有人壽保險保單的部分或全部資金，或使用或打算使用通過減少現有人壽保險保單的應付保費而節省的金額，以資助閣下購買新的人壽保險保單？ (勾選一項)
              </div>
              <div className={styles.optionsRow}>
                {getSelectedKey("usingExistingPolicyFundsForNewPolicy") === "A" && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    A. 是
                  </div>
                )}
                {getSelectedKey("usingExistingPolicyFundsForNewPolicy") === "B" && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    B. 否
                  </div>
                )}
                {getSelectedKey("usingExistingPolicyFundsForNewPolicy") === "C" && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    C. 不適用
                  </div>
                )}
                {!getSelectedKey("usingExistingPolicyFundsForNewPolicy") && <div className={cx(styles.mutedEmptyState, styles.fullWidth)}>未選擇任何選項</div>}
              </div>
              <div className={styles.descriptionNote}>
                <p>例如：此等資金或金額可能來自：</p>
                <ul className={styles.plainList}>
                  <li>a) 就閣下現有人壽保險保單作出退保/不分退保的安排，已獲得其退保價值；</li>
                  <li>b) 從閣下現有人壽保險保單中提取保單貸款（包括自動保費貸款/抵押轉讓（不包括保費融資））；</li>
                  <li>c) 從閣下現有人壽保險保單中提取保單價值（例如如實行紅利或贖回基金單位等）；</li>
                  <li>d) 容許閣下現有人壽保險保單失效（例如終止止付保費）；</li>
                  <li>e) 行使閣下現有人壽保險保單中"保費假期"的權利。</li>
                </ul>
              </div>
              <div className={styles.warningText}>*請注意，投保之後一年內在冷靜期之後退保的情況，亦有可能涉及轉保，請保險中介人清楚了解情況。</div>
            </div>

            {(getSelectedKey("usingExistingPolicyFundsForNewPolicy") === "A" || getSelectedKey("usingExistingPolicyFundsForNewPolicy") === "B") && (
              <div className={styles.recordCard}>
                <div className={styles.bottomMargin16}>
                  <div className={cx(styles.questionTitle, styles.bottomMargin8)}>1.A 若上方問題之答案為「是」或「尚未決定」者，保險中介人的評估和意見：</div>
                  <div className={styles.verticalList8}>
                    {transferAssessmentOptions
                      .filter((option) => getSelectedKey("intermediaryAssessmentAndComments") === option.value)
                      .map((option) => (
                        <div key={option.value} className={cx(styles.choiceCard, styles.choiceCardSm)}>
                          {option.label}
                          {option.value === "B" && <div className={styles.choiceCardDetail}>詳述: {getInputValue("intermediaryAssessmentAndCommentsOther") || "-"}</div>}
                        </div>
                      ))}
                    {!getSelectedKey("intermediaryAssessmentAndComments") && <div className={styles.mutedEmptyState}>未選擇任何選項</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CustomCard>
        {/* Card 4: 六、產品介紹及選擇結果 */}
        <CustomCard
          title="六、產品介紹及選擇結果"
          bordered={false}
          className={cx(styles["detail-card"], isZuuMode && styles.zuuDetailCard)}
        >
          {/* 六、產品介紹及選擇結果 */}
          <div>
            {/* Display selected products */}
            <div className={styles.productsList}>
              {(() => {
                // Helper to derive fields from FNA - matching FinancialForm logic
                const getDerivedFields = () => {
                  const q1Keys = getSelectedKeys("insurancePurchaseObjectives");

                  // Build objective string from all selected options
                  const objectiveLabels: string[] = [];
                  if (q1Keys.includes("A")) objectiveLabels.push("為應付不時之需提供財務保障");
                  if (q1Keys.includes("B")) objectiveLabels.push("為應付醫療保健需要");
                  if (q1Keys.includes("C")) objectiveLabels.push("為未來提供定期的收入");
                  if (q1Keys.includes("D")) objectiveLabels.push("為未來需要做儲蓄");
                  if (q1Keys.includes("E")) objectiveLabels.push("投資");
                  if (q1Keys.includes("F")) objectiveLabels.push("其他");
                  const objective = objectiveLabels.join("、");

                  const q4_1 = getSelectedKey("willingPremiumPaymentPeriodRange");
                  let paymentTerm = "";

                  if (q4_1) {
                    const paymentTermMap: Record<string, string> = {
                      A: "2-5年",
                      B: "6-10年",
                      C: "11-15年",
                      D: "16-20年",
                      E: "超過20年",
                      F: "終身",
                    };

                    if (q4_1 === "G") {
                      // For option G, show "一次性付款不超過 + amount"
                      const singlePaymentLimit = getInputValue("singlePremiumPaymentLimit");
                      const formattedAmount = singlePaymentLimit ? Number(singlePaymentLimit).toLocaleString("en-US") : "0";
                      paymentTerm = `一次性付款不超過 ${formattedAmount} HKD`;
                    } else {
                      paymentTerm = paymentTermMap[q4_1] || "";
                    }
                  }

                  const q2 = getSelectedKey("policyTargetExpectedTime");
                  const benefitTerm = timePeriodOptions.find((opt) => opt.value === q2)?.label;

                  return { objective, paymentTerm, benefitTerm };
                };

                const derived = getDerivedFields();

                return financial.chooseProducts && financial.chooseProducts.length > 0 ? (
                  financial.chooseProducts
                    .filter((p: any) => p.choose)
                    .map((p: any, index: number) => {
                      return (
                        <div key={index} className={styles.productCard}>
                          <div className={styles.productTitle}>介紹的產品{index + 1}</div>
                          <div className={styles.gridTwo12}>
                            <div>
                              <div className={styles.fieldLabel}>產品計劃</div>
                              <div className={styles.fieldValue}>{p.productName || "-"}</div>
                            </div>
                            {index === 0 && (
                              <>
                                <div>
                                  <div className={styles.fieldLabel}>選購產品目標</div>
                                  <div className={styles.fieldValue}>{derived.objective || "-"}</div>
                                </div>
                                <div>
                                  <div className={styles.fieldLabel}>選購產品的繳付年期</div>
                                  <div className={styles.fieldValue}>{derived.paymentTerm || "-"}</div>
                                </div>
                                <div>
                                  <div className={styles.fieldLabel}>目標得益/保障年期</div>
                                  <div className={styles.fieldValue}>{derived.benefitTerm || "-"}</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div className={styles.mutedEmptyState}>未選擇任何產品</div>
                );
              })()}
            </div>

            {/* Only provide one product question */}
            <div className={styles.splitSection}>
              <div className={styles.rowBetween}>
                <span className={styles.fontWeight500}>是否只能提供一款保險產品</span>
                <span className={financial.onlyProvideOneProduct ? styles.statusPositive : styles.statusMuted}>
                  {financial.onlyProvideOneProduct ? "是" : "否"}
                </span>
              </div>
              {financial.onlyProvideOneProduct && (
                <div className={styles.topMargin8}>
                  <div className={styles.fieldLabel}>若上方問題之答案為「是」者，請在下方填寫只能提供一家保險公司建議之原因：</div>
                  <div className={styles.fieldValue}>{financial.onlyProvideOneProductReason || "-"}</div>
                </div>
              )}
            </div>
          </div>
        </CustomCard>

        {/* Card 5: 七、建議及原因 */}
        <CustomCard title="七、建議及原因" bordered={false} className={styles["detail-card"]}>
          {/* 七、建議及原因 */}
          <div>
            {/* Question 1: Suitability Deviation */}
            <div className={styles.questionBlock}>
              <div className={cx(styles.questionTitle, styles.bottomMargin8)}>1. 有沒有出現合適性偏差？</div>
              <div className={styles.optionsRow}>
                {getSelectedKey("suitabilityDeviationOccurred") === "A" && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    A. 有
                  </div>
                )}
                {getSelectedKey("suitabilityDeviationOccurred") === "B" && (
                  <div className={cx(styles.choiceCard, styles.choiceCardSm, styles.choiceCardFlex)}>
                    B. 沒有
                  </div>
                )}
                {!getSelectedKey("suitabilityDeviationOccurred") && <div className={cx(styles.mutedEmptyState, styles.fullWidth)}>未選擇任何選項</div>}
              </div>

              {/* Nested question 1.1 - If answer is "有" */}
              {getSelectedKey("suitabilityDeviationOccurred") === "A" && (
                <div className={styles.detailIndent16}>
                  <div className={cx(styles.questionTitle, styles.bottomMargin8, styles.bodyTextBase)}>1.1 若上方問題之答案為「有」者，請選擇原因，（勾選一項）：</div>
                  <div className={styles.verticalList8}>
                    {(() => {
                      const yesOptions = [
                        { value: "A", label: "a. 購買保險產品的目標" },
                        { value: "B", label: "b. 目標利益/保障期的預期時間" },
                        { value: "C", label: "c. 能夠及願意繳付的保費佔所有收入來源" },
                        { value: "D", label: "d. 能夠及願意為保單支付保費的年期" },
                        { value: "E", label: "e. 保障所需要的金額" },
                        {
                          value: "F",
                          label: "f. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在以上各方面及以下原因取得平衡。",
                        },
                      ];

                      const selected = yesOptions.find((opt) => opt.value === getSelectedKey("suitabilityDeviationFollowUpYesOptions"));
                      return selected ? (
                        <div className={cx(styles.choiceCard, styles.choiceCardSm)}>
                          {selected.label}
                        </div>
                      ) : (
                        <div className={styles.mutedEmptyState}>未選擇任何選項</div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Nested question 1.2 - If answer is "沒有" */}
              {getSelectedKey("suitabilityDeviationOccurred") === "B" && (
                <div className={styles.detailIndent16}>
                  <div className={cx(styles.questionTitle, styles.bottomMargin8, styles.bodyTextBase)}>1.2 若上方問題之答案為「沒有」者，請選擇原因，（勾選一項）：</div>
                  <div className={styles.verticalList8}>
                    {(() => {
                      const noOptions = [
                        { value: "A", label: "a. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出" },
                        { value: "B", label: "b. 此建議是考慮到申請人的理財目標、全面保障的需要、各需要的優先重要性及申請人的財務預算而作出，在與申請人商討後，申請人期望在各方面取得平衡" },
                      ];

                      const selected = noOptions.find((opt) => opt.value === getSelectedKey("suitabilityDeviationFollowUpNoOptions"));
                      return selected ? (
                        <div className={cx(styles.choiceCard, styles.choiceCardSm)}>
                          {selected.label}
                        </div>
                      ) : (
                        <div className={styles.mutedEmptyState}>未選擇任何選項</div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Question 2: Reason for Recommending Despite Deviation - Only show if answer to Q1 is "有" */}
            {getSelectedKey("suitabilityDeviationOccurred") === "A" && (
              <div className={styles.questionBlock}>
                <div className={cx(styles.questionTitle, styles.bottomMargin8)}>2. 雖然有合適性偏差出現，但仍然介紹此產品的原因是：（勾選一項）</div>
                <div className={styles.verticalList8}>
                  {(() => {
                    const reasonOptions = [
                      {
                        value: "A",
                        label: "A. 因現在供款預算原因，這保險計劃的金額未能達到保險或儲蓄的全面需求，但現在建議的保險，已經符合年期及供款金額等要求，能夠邁向至全面的保障及儲蓄目標",
                      },
                      { value: "B", label: "B. 其他" },
                    ];

                    const selected = reasonOptions.find((opt) => opt.value === getSelectedKey("reasonForRecommendingDespiteDeviation"));
                    return selected ? (
                      <div className={cx(styles.choiceCard, styles.choiceCardSm)}>
                        {selected.label}
                        {selected.value === "B" && <div className={styles.choiceCardDetail}>其他原因: {getInputValue("otherReasonForRecommendation") || "-"}</div>}
                      </div>
                    ) : (
                      <div className={styles.mutedEmptyState}>未選擇任何選項</div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </CustomCard>
      </div>
    );
  };

  return currentFinancialStep === "financial-info"
    ? renderFinancialInfo()
    : renderFnaInfo();
}
